import { formatCurrency } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, LOCALE_ID, OnInit, ViewChild, inject } from "@angular/core";
import { formatAbbreviatedNumber, percentScale } from "@tylertech/common-components-module";
import { getLoggerFor } from "@tylertech/logging";
import { CommonChartData } from "@tylertech/tyler-api-types";
import { Chart, ChartConfiguration, ChartConfigurationCustomTypesPerDataset, ChartType, Color, InteractionModeMap, ScaleOptions, ScaleType, TooltipItem } from "chart.js";
import { BaseSeries, ChartJsConfigBuilder, rgb, Series } from "@tylertech/chart-js";

function isIndexAxis(value: string | undefined): value is 'x' | 'y'
{
    return value && /^x|y$/.test(value);
}

@Component({
    selector: "lgd-chart",
    template: `<div #container class="chart-container">
    <canvas #chart></canvas>
</div>`,
    styles: [`
div.chart-container {
    position: relative;
    height: 100%;
    width: 100%;
}
`],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LgdChartComponent implements OnInit, AfterViewInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly changeDetectionRef = inject(ChangeDetectorRef);

    @ViewChild("container") readonly containerRef: ElementRef<HTMLDivElement>;
    @ViewChild("chart") readonly canvasRef: ElementRef<HTMLCanvasElement>;

    @Input() readonly data: CommonChartData;

    private readonly log = getLoggerFor("LgdChartComponent");

    private chartConfig: ChartConfiguration | ChartConfigurationCustomTypesPerDataset;
    private chartObj: Chart;

    ngOnInit(): void
    {
        if (!this.data)
        {
            throw new Error("[data] is required.");
        }

        const percentBased = this.data.XAxes[0].PercentBased || this.data.YAxes[0].PercentBased;
        const currencyBased = this.data.CurrencyBased;
        const chartType = getChartType(this.data.Type);

        const configBuilder = new ChartJsConfigBuilder<Record<string, number>>(this.data.Title, chartType)
            .setResponsive(true)
            .setMaintainAspectRatio(this.data.MaintainAspectRatio)
            .setIndexAxis(isIndexAxis(this.data.IndexAxis) ? this.data.IndexAxis : undefined)
            .setTitle({
                // display: false,
                display: true,
                text: this.data.Title,
            })
            .setLegend({
                display: this.data.ShowLegend,
                position: "bottom",
                labels: { boxWidth: 12 },
            })
            .setTooltips({
                mode: this.data.TooltipMode as (keyof InteractionModeMap),
                xAlign: "right",
                callbacks: {
                    title: (tooltipItems: TooltipItem<ChartType>[]): string | string[] =>
                    {
                        const firstDataSetIndex = tooltipItems[0].datasetIndex;
                        const dataIndex = tooltipItems[0].dataIndex;
                        const tooltips = this.data.Rows[firstDataSetIndex].Tooltips;
                        return tooltips?.length
                            ? tooltips[dataIndex]
                            : this.data.Labels[dataIndex];
                    },
                    label: (tooltipItem: TooltipItem<ChartType>): string | string[] =>
                    {
                        const dataSetIndex = tooltipItem.datasetIndex;
                        const tooltips = this.data.Rows[dataSetIndex].Tooltips;
                        if (!tooltips?.length)
                        {
                            return "";
                        }

                        const label = this.data.Rows[dataSetIndex].Name;
                        const value = tooltipItem.raw;
                        if (this.data.TooltipMode !== "index")
                        {
                            return formatCurrency(Number(value), this.locale, "$");
                        }

                        const formatted = currencyBased
                            ? formatCurrency(Number(value), this.locale, "$")
                            : value;
                        const symbol = percentBased ? "%" : "";
                        const returnLabel = `${label}: ${formatted}${symbol}`;
                        return returnLabel;
                    },
                },
            })
            // .setElements({ line: { fill: false } })
            .setCategories(this.data.Labels);

        if (!["pie", "doughnut"].includes(chartType))
        {
            configBuilder.addScales(
                [
                    ...this.data.XAxes,
                    ...this.data.YAxes
                ].reduce((scales, axis) =>
                {
                    const scale: ScaleOptions<ScaleType> = {
                        type: axis.Type as ScaleType,
                        display: axis.Display,
                        position: isScalePosition(axis.Position) ? axis.Position : undefined,
                    };
                    switch (axis.Type)
                    {
                        case "linear":
                        case "category": {
                            const typedScale = scale as ScaleOptions<"category" | "linear">;
                            typedScale.stacked = axis.Stacked;
                            typedScale.min = 0;
                            typedScale.max = axis.PercentBased ? 100 : undefined;
                            typedScale.ticks = axis.PercentBased
                                ? {
                                    callback: percentScale,
                                    maxTicksLimit: 10,
                                }
                                : this.data.Type === "horizontalBar"
                                    ? {
                                        callback: abbreviateNumber,
                                    }
                                    : {};
                            break;
                        }
                        case "logarithmic":
                        case "time":
                        case "timeseries":
                        case "radialLinear":
                        default:
                            this.log.warning(`Unsupported scale type on Axis "${axis.Id}"; was ${axis.Type}`);
                            break;
                    }

                    this.log.debug(`created scale for axis`, { axis, scale });
                    scales[axis.Id] = scale;
                    return scales;
                },
                    {}));
        }

        configBuilder
            .addSeries(
                this.data.Rows.map(row =>
                {
                    const mostlyOpaqueColors: Color[] = [];
                    let mostlyOpaqueColor: Color;

                    const opaqueColors: Color[] = [];
                    let opaqueColor: Color;

                    const useListOfColors = row.Colors?.length > 1;
                    if (!useListOfColors)
                    {
                        const color = row.Colors[0];
                        mostlyOpaqueColor = rgb(color.R, color.G, color.B, 1);
                        opaqueColor = rgb(color.R, color.G, color.B, 1.0);
                    }
                    else
                    {
                        for (let j = 0; j < row.Colors.length; j++)
                        {
                            const color = row.Colors[j];
                            mostlyOpaqueColors.push(rgb(color.R, color.G, color.B, 1));
                            opaqueColors.push(rgb(color.R, color.G, color.B, 1.0));
                        }
                    }
                    const colorSettings = useListOfColors ? mostlyOpaqueColors : mostlyOpaqueColor;
                    const hoverColorSettings = useListOfColors ? opaqueColors : opaqueColor;

                    const series: BaseSeries<Record<string, number>> = {
                        name: row.Name,
                        backgroundColor: colorSettings,
                        border: { color: colorSettings },
                        hover: {
                            backgroundColor: hoverColorSettings,
                            borderColor: hoverColorSettings,
                        },
                        getValue: (v, _i) => v[row.Name],
                    };

                    switch ((row.Type ?? chartType) as ChartType)
                    {
                        case undefined:
                        case "line": {
                            const lineSeries = series as Series<unknown, "line">;

                            lineSeries.type = "line";
                            lineSeries.fill = row.Fill;
                            lineSeries.tension = 0;
                            // lineSeries.point = {
                            //     borderColor: rgb(255, 255, 255, 0),
                            // };
                            // NOTE: CommonChartData really only supports single axis for both X and Y
                            lineSeries.xAxisId = "x-axis";
                            lineSeries.yAxisId = "y-axis";
                            break;
                        }
                        case "bar": {
                            const barSeries = series as Series<unknown, "bar">;

                            barSeries.type = "bar";
                            // NOTE: CommonChartData really only supports single axis for both X and Y
                            barSeries.xAxisId = "x-axis";
                            barSeries.yAxisId = "y-axis";
                            break;
                        }
                        case "pie": {
                            const pieSeries = series as Series<unknown, "pie">;

                            pieSeries.type = "pie";
                            break;
                        }
                        case "doughnut": {
                            const doughnutSeries = series as Series<unknown, "doughnut">;

                            doughnutSeries.type = "doughnut";
                            break;
                        }
                        case "bubble":
                        case "polarArea":
                        case "radar":
                        case "scatter":
                        default: {
                            this.log.warning(`Unsupported dataset type on dataset "${row.Name}"; was ${row.Type}`);
                            const unknownSeries = series as Series<unknown, ChartType>;
                            unknownSeries.type = row.Type as ChartType;
                            break;
                        }
                    }

                    this.log.debug(`created series for dataRow`, { row, series });
                    return series as Series;
                }));

        const largestRow = Math.max(...this.data.Rows.map(x => x.Values.length));
        const data: Array<Record<string, number>> = [];
        for (let i = 0; i < largestRow; i++)
        {
            const item: Record<string, number> = {};
            this.data.Rows.forEach(r =>
            {
                item[r.Name] = i <= r.Values.length ? r.Values[i] : 0;
            });

            data.push(item);
        }

        this.chartConfig = configBuilder.build(data);

        function abbreviateNumber(value: number): string
        {
            return formatAbbreviatedNumber(
                value > -10 && value < 10
                    ? value.toFixed(2)
                    : value.toFixed(0));
        }

        function isScalePosition(value: string): value is 'left' | 'top' | 'right' | 'bottom' | 'center'
        {
            return /left|top|right|bottom|center/.test(value);
        }

        function getChartType(type: string): ChartType
        {
            return (type === "horizontalBar"
                ? "bar"
                : type as ChartType) ?? "bar";
        }
    }

    ngAfterViewInit(): void
    {
        const container = this.containerRef.nativeElement;
        const chartCanvas = this.getCanvas();

        // todo - use data.Height and data.width to calculate
        chartCanvas.height = this.data.Height
            ? (container.offsetWidth * this.data.Height) / this.data.Width
            : container.offsetHeight;
        chartCanvas.width = container.offsetWidth;
        const context = chartCanvas.getContext("2d");

        this.chartObj = new Chart(context, this.chartConfig);
        this.changeDetectionRef.markForCheck();
    }

    getCanvas(): HTMLCanvasElement
    {
        return this.canvasRef.nativeElement;
    }
}
